import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { useWorkspaces, WorkspacePicker } from '../components/useWorkspaces'
import { rpc, useRpcEvent } from '../api/rpc'
import { useSession } from '../api/session'
import type { AvailableTool, ModelList, Routine, RoutineDefinition, RoutineRun, RoutineTrigger } from '../api/types'
import { ExtractorPanel } from '../components/ExtractorPanel'
import { RunStatusBadge } from '../components/RunStatusBadge'
import { RunSpark } from '../components/RunSpark'
import { useModels } from '../components/useModels'
import { Button } from '../components/ui/Button'
import { Badge, Card, EmptyState, InfoRow, PageHeader } from '../components/ui/Card'
import { Count, FilterChips, Row, RowMenu, SearchBox, SegmentedFilter, Table, Td, Th, Toolbar, type Rail } from '../components/ui/DataTable'
import { Field, Input, Checkbox, Switch, Textarea, chipClass } from '../components/ui/Field'
import { Select, type SelectOption } from '../components/ui/Select'
import { Modal } from '../components/ui/Modal'
import { useToast } from '../components/ui/Toast'
import { formatDateTime, formatRelative, formatUsd } from '../lib/format'
import { cn } from '../lib/cn'
import { IconClock, IconExpand, IconInbox, IconLink, IconPaperPlane, IconPlus } from '../components/icons'
import { MarkdownOverlayEditor } from '../components/MarkdownOverlayEditor'
import { CollectionChecklist, useReadableCollections } from '../components/chat/KbScopePicker'
import { useConnectors } from './ConnectorsPage'
import { confirmDialog } from '../components/ui/Confirm'
import { DEFAULT_SCHEDULE, describeSchedule, exprFor, guessSchedule, PRESETS, WEEKDAY_LABEL, WEEKDAY_ORDER, type Schedule } from './automationSchedule'

export function triggerSummary(tr: RoutineTrigger, t: (k: string, o?: Record<string, unknown>) => string): string {
  switch (tr.kind) {
    case 'cron': {
      const friendly = describeSchedule(tr.expr, t)
      return friendly ? `${friendly} · ${tr.tz}` : t('automations.trigger.cronSummary', { expr: tr.expr, tz: tr.tz })
    }
    case 'once': return t('automations.trigger.onceSummary', { at: tr.at.replace('T', ' '), tz: tr.tz })
    case 'telegram': return t('automations.trigger.telegramSummary')
    case 'webhook': return t('automations.trigger.webhookSummary')
    case 'gmail': return t('automations.trigger.gmailSummary', { query: tr.query, n: tr.intervalMinutes })
  }
}

type ListFilter = 'all' | 'mine' | 'team' | 'failed'
type RoutineGroup = 'attention' | 'scheduled' | 'event' | 'off'

/** Rail colour of a tool row: what the permission means at a glance (same scale as Settings). */
const POLICY_RAIL: Record<AvailableTool['defaultPolicy'], Rail> = { allow: 'ok', ask: 'attention', deny: 'error' }
interface Overview { enabled: number; total: number; runs7d: number; failed7d: number; waitingApproval: number; costPeriodUsd: number; periodStart?: number }

/** A run that fires within the hour turns its "in 2 hours" line amber. */
const SOON_MS = 3_600_000

/** Icon per trigger kind, tinted by kind: the list can be scanned by "what fires this". */
const TRIGGER_TILE: Record<RoutineTrigger['kind'], string> = {
  cron: 'bg-ginger-100 text-ginger-600',
  once: 'bg-ginger-100 text-ginger-600',
  telegram: 'bg-mint-50 text-mint-500',
  webhook: 'bg-amber-50 text-amber-700',
  gmail: 'bg-red-50 text-red-600'
}

function TriggerIcon({ kind }: { kind: RoutineTrigger['kind'] }) {
  const Icon = kind === 'telegram' ? IconPaperPlane : kind === 'webhook' ? IconLink : kind === 'gmail' ? IconInbox : IconClock
  return (
    <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] max-[860px]:h-8 max-[860px]:w-8', TRIGGER_TILE[kind])} aria-hidden="true">
      <Icon width={18} height={18} />
    </span>
  )
}

/**
 * Which group a routine lands in. Order is the order of the question the page answers: what needs me
 * (failed / waiting for approval), then what runs on a clock, then what waits for an event, then what
 * is switched off (docs/web-ui.md §3).
 */
function groupOf(r: Routine): RoutineGroup {
  if (!r.enabled) return 'off'
  const status = r.lastRun?.status
  if (status === 'failed' || status === 'waiting-approval') return 'attention'
  return r.trigger.kind === 'cron' || r.trigger.kind === 'once' ? 'scheduled' : 'event'
}

const GROUP_ORDER: RoutineGroup[] = ['attention', 'scheduled', 'event', 'off']

/**
 * One routine. A grid row rather than a `<Table>` row: it carries a sparkline, two-line dates and a
 * switch, none of which are cells in a ledger. Columns drop away from the right as the window narrows
 * (sparkline below 1100 px, next-run and actions below 860 px).
 */
function RoutineRow({ r, t, language, canEdit, owner, onRunNow, onOpen, onDuplicate, onRemove, onToggle }: {
  r: Routine
  t: (key: string, options?: Record<string, unknown>) => string
  language: string
  canEdit: boolean
  owner: boolean
  onRunNow(): void
  onOpen(): void
  onDuplicate(): void
  onRemove(): void
  onToggle(enabled: boolean): void
}) {
  const eventTrigger = r.trigger.kind === 'telegram' || r.trigger.kind === 'webhook'
  const soon = r.nextRunAt !== null && r.nextRunAt - Date.now() < SOON_MS
  const rail = r.enabled && r.lastRun?.status === 'failed' ? 'bg-red-600'
    : r.enabled && r.lastRun?.status === 'waiting-approval' ? 'bg-amber-400' : undefined
  return (
    <div className="relative grid grid-cols-[36px_minmax(0,1fr)_150px_116px_116px_40px_120px] items-center gap-3.5 border-t border-line px-4 py-2.5 pl-4 transition-colors first:border-t-0 hover:bg-cream-100 max-[1100px]:grid-cols-[36px_minmax(0,1fr)_150px_116px_40px_120px] max-[860px]:grid-cols-[32px_minmax(0,1fr)_104px_40px] max-[860px]:gap-2.5">
      {rail && <span className={cn('absolute inset-y-0 left-0 w-[3px]', rail)} aria-hidden="true" />}
      <TriggerIcon kind={r.trigger.kind} />
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <Link to={`/automations/${r.id}`} className="truncate font-medium text-ink-100 hover:text-ginger-600">{r.name}</Link>
          {r.access && r.access !== 'owner' && (
            <Badge tone="accent" className="h-5">{t('teams.shared')} · {t(`teams.role_${r.access}`)}</Badge>
          )}
        </div>
        <p className="truncate text-[12.5px] text-ink-500">{triggerSummary(r.trigger, t)}</p>
      </div>
      <div className="leading-tight max-[860px]:hidden">
        {r.nextRunAt ? (
          <>
            <div className="text-[12.5px] text-ink-300 tabular-nums">{formatDateTime(r.nextRunAt, language)}</div>
            <div className={cn('mt-0.5 text-[11.5px]', soon ? 'text-amber-700' : 'text-ink-500')}>{formatRelative(r.nextRunAt, language)}</div>
          </>
        ) : (
          <span className="text-[12.5px] text-ink-500">{r.enabled && eventTrigger ? t('automations.onEvent') : '—'}</span>
        )}
      </div>
      <RunSpark runs={r.recentRuns ?? []} label={t('automations.spark', { count: r.recentRuns?.length ?? 0 })} className="max-[1100px]:hidden" />
      <div className="justify-self-start">
        {r.lastRun ? <RunStatusBadge status={r.lastRun.status} /> : !r.enabled ? <Badge>{t('automations.off')}</Badge> : <span className="text-[12.5px] text-ink-500">—</span>}
      </div>
      <div>
        {canEdit && (
          <Switch label={`${t('automations.enabled')}: ${r.name}`} checked={r.enabled} onChange={onToggle} />
        )}
      </div>
      <div className="flex items-center justify-end gap-1 max-[860px]:hidden">
        {canEdit && <Button size="sm" variant="secondary" onClick={onRunNow}>{t('automations.runNow')}</Button>}
        <RowMenu
          label={`${t('common.more')}: ${r.name}`}
          items={[
            { label: t('common.open'), onSelect: onOpen },
            ...(canEdit ? [{ label: t('automations.runNow'), onSelect: onRunNow }] : []),
            ...(owner ? [
              { label: t('automations.duplicate'), onSelect: onDuplicate, divider: true },
              { label: t('common.delete'), onSelect: onRemove, danger: true }
            ] : [])
          ]}
        />
      </div>
    </div>
  )
}

export function AutomationsList() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const [items, setItems] = useState<Routine[] | null>(null)
  const [overview, setOverview] = useState<Overview | null>(null)
  const [filter, setFilter] = useState<ListFilter>('all')
  const [query, setQuery] = useState('')
  const load = useCallback(() => {
    void rpc.call<{ items: Routine[] }>('routines.list').then(r => setItems(r.items))
    void rpc.call<Overview>('routines.overview').then(setOverview).catch(() => undefined)
  }, [])
  useEffect(() => { load() }, [load])
  useRpcEvent('routine.updated', load)
  useRpcEvent('routine.deleted', load)
  useRpcEvent('routine.run', load)
  const fail = (err: unknown) => toast((err as Error).message, 'error')
  const all = items ?? []
  const matches = (r: Routine, f: ListFilter) => f === 'all' ? true
    : f === 'mine' ? (r.access ?? 'owner') === 'owner'
      : f === 'team' ? Boolean(r.access && r.access !== 'owner')
        : r.lastRun?.status === 'failed'
  const count = (f: ListFilter) => all.filter(r => matches(r, f)).length
  const q = query.trim().toLowerCase()
  const shown = all.filter(r => matches(r, filter)
    && (q ? `${r.name} ${triggerSummary(r.trigger, t)}`.toLowerCase().includes(q) : true))
  const filters: { value: ListFilter; label: string }[] = [
    { value: 'all', label: t('automations.filter.all') }, { value: 'mine', label: t('automations.filter.mine') },
    { value: 'team', label: t('automations.filter.team') }, { value: 'failed', label: t('automations.filter.failed') }
  ]
  const groups = GROUP_ORDER
    .map(key => ({ key, items: shown.filter(r => groupOf(r) === key) }))
    .filter(g => g.items.length > 0)
  const successRate = overview && overview.runs7d > 0 ? Math.round(((overview.runs7d - overview.failed7d) / overview.runs7d) * 100) : null
  const runNow = (r: Routine) => void rpc.call('routines.runNow', { routineId: r.id }).then(() => toast(t('automations.queued'), 'success')).catch(fail)
  const duplicate = async (r: Routine) => {
    try {
      const res = await rpc.call<{ routine: Routine }>('routines.duplicate', { routineId: r.id })
      toast(t('automations.duplicated'), 'success')
      load()
      navigate(`/automations/${res.routine.id}`)
    } catch (e) { fail(e) }
  }
  const remove = async (r: Routine) => {
    if (!await confirmDialog(t('automations.confirmDelete', { name: r.name }))) return
    await rpc.call('routines.delete', { routineId: r.id }).then(load).catch(fail)
  }
  return (
    <div className="mx-auto w-full max-w-[1120px] px-6 pb-12 pt-9 max-sm:px-4 max-sm:pb-24 max-sm:pt-6">
      <PageHeader title={t('automations.title')} subtitle={t('automations.subtitle')}
        actions={
          <Link to="/automations/new" className="inline-flex h-9 items-center gap-2 rounded-lg bg-ginger-500 px-3.5 text-[13.5px] font-medium text-white transition-colors hover:bg-ginger-400 active:bg-ginger-600 max-sm:hidden">
            <IconPlus width={16} height={16} />{t('automations.new')}
          </Link>
        } />
      {/* Numbers as one line of facts, not four tiles: they are read once, then the list matters. */}
      {overview && overview.total > 0 && (
        <div className="-mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-ink-300 [&>span+span]:border-l [&>span+span]:border-line [&>span+span]:pl-5">
          <span><b className="font-semibold text-ink-100 tabular-nums">{overview.enabled}</b> / {overview.total} {t('automations.fact.enabled')}</span>
          <span><b className="font-semibold text-ink-100 tabular-nums">{overview.runs7d}</b> {t('automations.fact.runs7d')}</span>
          {successRate !== null && <span><b className="font-semibold text-ink-100 tabular-nums">{successRate}%</b> {t('automations.fact.success')}</span>}
          <span className={overview.waitingApproval ? 'text-amber-700' : undefined}>
            <Link to="/inbox" className="hover:underline"><b className="font-semibold tabular-nums">{overview.waitingApproval}</b> {t('automations.fact.waiting')}</Link>
          </span>
          <span><b className="font-semibold text-ink-100 tabular-nums">{formatUsd(overview.costPeriodUsd)}</b> {t('automations.fact.cost')}</span>
        </div>
      )}
      {all.length > 0 && (
        <div className="mt-5">
          <SegmentedFilter<ListFilter>
            value={filter} onChange={setFilter} label={t('automations.filter.label')}
            options={filters.map(f => ({ ...f, count: count(f.value) }))}
            trailing={<SearchBox value={query} onChange={setQuery} placeholder={t('automations.search')} className="h-8 text-[13px] sm:min-w-[240px] sm:max-w-[240px]" />}
          />
        </div>
      )}
      {items?.length === 0 && <EmptyState title={t('automations.empty')} body={t('automations.emptyBody')} action={<Link to="/automations/new"><Button>{t('automations.new')}</Button></Link>} />}
      {all.length > 0 && shown.length === 0 && <p className="py-10 text-center text-sm text-ink-400">{t('automations.filter.none')}</p>}
      {groups.map(g => (
        <section key={g.key} className="mt-6">
          <div className="mb-2 flex items-center gap-2">
            <h2 className={cn('font-mono text-[10.5px] font-medium uppercase tracking-[0.09em]', g.key === 'attention' ? 'text-amber-700' : 'text-ink-500')}>
              {t(`automations.group.${g.key}`)}
            </h2>
            <span className="font-mono text-[11px] text-ink-500 tabular-nums">{g.items.length}</span>
            <span className="h-px flex-1 bg-line" aria-hidden="true" />
          </div>
          <div className="overflow-hidden rounded-xl border border-line bg-cream-50">
            {g.items.map(r => (
              <RoutineRow
                key={r.id} r={r} t={t} language={i18n.language}
                canEdit={r.access !== 'viewer'} owner={(r.access ?? 'owner') === 'owner'}
                onRunNow={() => runNow(r)}
                onOpen={() => navigate(`/automations/${r.id}`)}
                onDuplicate={() => void duplicate(r)}
                onRemove={() => void remove(r)}
                onToggle={v => void rpc.call('routines.setEnabled', { routineId: r.id, enabled: v }).catch(fail)}
              />
            ))}
          </div>
        </section>
      ))}
      <Link to="/automations/new" className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))] right-5 z-30 inline-flex h-14 items-center gap-2 rounded-full bg-ginger-500 px-5 font-semibold text-white sm:hidden">
        <IconPlus width={18} height={18} />{t('automations.new')}
      </Link>
    </div>
  )
}

/** Marks a required label, as in the design. */
function Required() {
  return <span aria-hidden="true" className="text-red-600"> *</span>
}

/**
 * Caption + a group of toggle buttons (weekdays, days of the month). Not `<Field>`: that renders a
 * `<label for>` which needs a single control to point at, and there is none here — the buttons are
 * named by the group's own `aria-label`.
 */
function ChipGroup({ label, className = 'flex flex-wrap gap-2', children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[12.5px] font-medium text-ink-300">{label}<Required /></p>
      <div role="group" aria-label={label} className={className}>{children}</div>
    </div>
  )
}

interface Draft extends Schedule {
  name: string
  enabled: boolean
  kind: RoutineTrigger['kind']
  at: string
  tz: string
  connectorId: string
  chatIds: string
  textPrefix: string
  noTextReply: string
  gmailQuery: string
  gmailInterval: number
  instructions: string
  mode: RoutineDefinition['mode']
  model: string
  toolsMode: 'default' | 'custom'
  tools: string[]
  toolPolicy: Record<string, 'allow' | 'ask' | 'deny'>
  kbCollections: string[]
  limits: RoutineDefinition['limits']
  session: RoutineDefinition['session']
  missedRunPolicy: RoutineDefinition['missedRunPolicy']
  notifyFailure: boolean
  notifySuccess: boolean
}

function toDraft(r: Routine | null, tz: string): Draft {
  const tr = r?.trigger
  const guess = tr?.kind === 'cron' ? guessSchedule(tr.expr) : {}
  return {
    ...DEFAULT_SCHEDULE,
    ...guess,
    ...(tr?.kind === 'cron' ? { custom: tr.expr } : {}),
    name: r?.name ?? '', enabled: r?.enabled ?? true, kind: tr?.kind ?? 'cron', at: tr?.kind === 'once' ? tr.at : '',
    tz: tr && 'tz' in tr ? tr.tz : tz,
    connectorId: tr?.kind === 'telegram' || tr?.kind === 'gmail' ? tr.connectorId : '',
    gmailQuery: tr?.kind === 'gmail' ? tr.query : 'in:inbox', gmailInterval: tr?.kind === 'gmail' ? tr.intervalMinutes : 5, chatIds: tr?.kind === 'telegram' ? (tr.filter?.chatIds ?? []).join(', ') : '',
    textPrefix: tr?.kind === 'telegram' ? tr.filter?.textPrefix ?? '' : '', noTextReply: tr?.kind === 'telegram' ? tr.noTextReply ?? '' : '',
    instructions: r?.definition.instructions ?? '', mode: r?.definition.mode ?? 'agent', model: r?.definition.model ?? '',
    toolsMode: r ? 'custom' : 'default', tools: r?.definition.tools ?? [], toolPolicy: r?.definition.toolPolicy ?? {},
    kbCollections: r?.definition.kbCollections ?? [],
    limits: r?.definition.limits ?? { maxRunsPerHour: 60, maxStepsPerRun: 25, maxCostPerRunUsd: 0.5, timeoutSec: 600 },
    session: r?.definition.session ?? 'per-run', missedRunPolicy: r?.definition.missedRunPolicy ?? 'skip',
    notifyFailure: r?.definition.notify.onFailure ?? true, notifySuccess: r?.definition.notify.onSuccess ?? false
  }
}

function buildTrigger(d: Draft): RoutineTrigger {
  switch (d.kind) {
    case 'cron': return { kind: 'cron', expr: exprFor(d), tz: d.tz }
    case 'once': return { kind: 'once', at: d.at, tz: d.tz }
    case 'telegram': {
      const chatIds = d.chatIds.split(',').map(s => Number(s.trim())).filter(n => Number.isFinite(n) && n !== 0)
      return {
        kind: 'telegram', connectorId: d.connectorId,
        filter: { ...(chatIds.length ? { chatIds } : {}), ...(d.textPrefix ? { textPrefix: d.textPrefix } : {}), requireText: true, includeEdited: false },
        ...(d.noTextReply ? { noTextReply: d.noTextReply } : {})
      }
    }
    case 'webhook': return { kind: 'webhook' }
    case 'gmail': return { kind: 'gmail', connectorId: d.connectorId, query: d.gmailQuery.trim() || 'in:inbox', intervalMinutes: d.gmailInterval }
  }
}

/** Every model the owner can run on — admin-shared providers and their own keys. */
function modelOptions(models: ModelList | null, t: (k: string) => string): SelectOption[] {
  const options: SelectOption[] = [{ value: '', label: t('automations.defaultModel') }]
  for (const g of models?.groups ?? []) {
    if (g.models.length === 0) continue
    g.models.forEach((m, i) => options.push({
      value: `${g.providerId}/${m}`,
      label: m,
      hint: `${g.name} · ${g.scope === 'personal' ? t('models.personal') : t('models.shared')}`,
      divider: i === 0
    }))
  }
  return options
}

export function AutomationEditor() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const { routineId } = useParams()
  const { user } = useSession()
  const { models } = useModels()
  const { items: connectors } = useConnectors()
  const readable = useReadableCollections()
  const [routine, setRoutine] = useState<Routine | null>(null)
  const [draft, setDraft] = useState<Draft | null>(routineId ? null : toDraft(null, user?.timezone ?? 'Asia/Ho_Chi_Minh'))
  const [available, setAvailable] = useState<AvailableTool[]>([])
  const [toolQuery, setToolQuery] = useState('')
  const [toolFilter, setToolFilter] = useState<'all' | 'on'>('all')
  const [preview, setPreview] = useState<{ runs?: number[]; error?: string }>({})
  const [runs, setRuns] = useState<RoutineRun[]>([])
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [ws, setWs] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)
  const [instructionsExpanded, setInstructionsExpanded] = useState(false)
  const { items: workspaces } = useWorkspaces()

  const loadRuns = useCallback(() => {
    if (routineId) void rpc.call<{ items: RoutineRun[] }>('routines.runs', { routineId, limit: 30 }).then(r => setRuns(r.items))
  }, [routineId])
  useEffect(() => {
    void rpc.call<{ items: AvailableTool[] }>('routines.availableTools').then(r => setAvailable(r.items))
    if (!routineId) return
    void rpc.call<Routine>('routines.get', { routineId }).then(r => { setRoutine(r); setDraft(toDraft(r, user?.timezone ?? 'Asia/Ho_Chi_Minh')) })
    loadRuns()
  }, [routineId, user?.timezone, loadRuns])
  useRpcEvent('routine.run', (r: RoutineRun) => { if (r.routineId === routineId) loadRuns() })

  const expr = draft ? exprFor(draft) : ''
  // Tool picker: search + "only what I selected", so a long connector list stays workable.
  const shownTools = useMemo(() => {
    if (!draft) return []
    const q = toolQuery.trim().toLowerCase()
    return available
      .filter(a => (toolFilter === 'on' ? draft.tools.includes(a.name) : true))
      .filter(a => (q ? a.name.toLowerCase().includes(q) || a.source.toLowerCase().includes(q) : true))
  }, [available, draft, toolQuery, toolFilter])
  useEffect(() => {
    if (!draft || draft.kind !== 'cron') return
    const h = setTimeout(() => {
      rpc.call<{ runs: number[] }>('routines.previewCron', { expr, tz: draft.tz }).then(r => setPreview({ runs: r.runs })).catch(err => setPreview({ error: (err as Error).message }))
    }, 300)
    return () => clearTimeout(h)
  }, [expr, draft?.tz, draft?.kind])

  const allModels = useMemo(() => modelOptions(models, t), [models, t])
  // Nothing ticked would silently fall back to day 1; say so instead.
  const emptyDays = (draft?.preset === 'weekly' && draft.weekdays.length === 0) || (draft?.preset === 'monthly' && draft.monthDays.length === 0)
  const telegramConnectors = connectors?.filter(c => c.kind === 'telegram') ?? []
  const googleConnectors = connectors?.filter(c => c.kind === 'google' && ((c.config.products as string[] | undefined) ?? []).includes('gmail')) ?? []

  const validate = useCallback((d: Draft): Record<string, string> => {
    const errs: Record<string, string> = {}
    if (!d.name.trim()) errs.name = t('automations.validation.nameRequired')
    if (!d.instructions.trim()) errs.instructions = t('automations.validation.instructionsRequired')

    // Trigger validation
    if (d.kind === 'cron') {
      if (d.preset === 'weekly' && d.weekdays.length === 0) errs.weekdays = t('automations.validation.pickWeekday')
      if (d.preset === 'monthly' && d.monthDays.length === 0) errs.monthDays = t('automations.validation.pickMonthDay')
      if (d.preset === 'custom' && !d.custom.trim()) errs.custom = t('automations.validation.customCronRequired')
      if (preview.error) errs.cronPreview = preview.error
    } else if (d.kind === 'once') {
      if (!d.at) {
        errs.at = t('automations.validation.atRequired')
      } else {
        const fire = new Date(d.at).getTime()
        if (Number.isFinite(fire) && fire <= Date.now()) {
          errs.at = t('automations.validation.atPast')
        }
      }
    } else if (d.kind === 'telegram') {
      if (!d.connectorId) errs.connectorId = t('automations.validation.botRequired')
    } else if (d.kind === 'gmail') {
      if (!d.connectorId) errs.connectorId = t('automations.validation.gmailRequired')
    }

    if (d.kind === 'cron' || d.kind === 'once') {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: d.tz })
      } catch {
        errs.tz = t('automations.validation.invalidTimezone')
      }
    }

    return errs
  }, [t, preview.error])

  if (!draft) return null

  const set = (p: Partial<Draft>) => {
    const updated = { ...draft, ...p }
    setDraft(updated)
    if (submitted) {
      setErrors(validate(updated))
    }
  }

  const modelLabel = allModels.find(o => o.value === draft.model)?.label ?? t('automations.defaultModel')
  const toolsLabel = draft.toolsMode === 'default' ? t('automations.toolsDefault') : t('automations.toolsCount', { count: draft.tools.length, total: available.length })
  const onceAt = draft.kind === 'once' && draft.at ? new Date(draft.at).getTime() : null
  // The summary answers "when will this run next" before saving, so a new cron shows its first fire.
  const nextRun = routine?.nextRunAt ?? (draft.kind === 'cron' ? preview.runs?.[0] ?? null : onceAt && Number.isFinite(onceAt) ? onceAt : null)

  const save = async () => {
    setSubmitted(true)
    const errs = validate(draft)
    setErrors(errs)
    const errorKeys = Object.keys(errs)
    if (errorKeys.length > 0) {
      const firstKey = errorKeys[0]
      const el = document.getElementById(`field-${firstKey}`) ?? document.querySelector(`[data-field="${firstKey}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLButtonElement) {
          el.focus()
        }
      }
      toast(t('automations.validation.summary', { count: errorKeys.length }), 'error')
      return
    }

    setSaving(true)
    const definition = {
      instructions: draft.instructions,
      mode: draft.mode,
      ...(draft.model ? { model: draft.model } : {}),
      ...(draft.toolsMode === 'custom' ? { tools: draft.tools, toolPolicy: Object.fromEntries(Object.entries(draft.toolPolicy).filter(([k]) => draft.tools.includes(k))) } : {}),
      // Always sent: routines.update merges with the stored definition, so omitting it would make
      // "untick every collection" (back to every readable collection) impossible.
      kbCollections: draft.kbCollections,
      session: draft.session, limits: draft.limits, missedRunPolicy: draft.missedRunPolicy,
      notify: { onFailure: draft.notifyFailure, onSuccess: draft.notifySuccess }
    }
    try {
      if (routine) {
        const r = await rpc.call<Routine>('routines.update', { routineId: routine.id, name: draft.name, enabled: draft.enabled, trigger: buildTrigger(draft), definition })
        setRoutine(r)
        toast(t('common.saved'), 'success')
      } else {
        const r = await rpc.call<{ routine: Routine; webhookUrl?: string }>('routines.create', { name: draft.name, enabled: draft.enabled, trigger: buildTrigger(draft), definition, ...(ws ? { workspaceId: ws } : {}) })
        if (r.webhookUrl) setWebhookUrl(r.webhookUrl)
        navigate(`/automations/${r.routine.id}`, { replace: true })
      }
    } catch (err) {
      toast((err as Error).message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 pb-12 pt-9 max-sm:px-4 max-sm:pt-6">
      <PageHeader
        title={routine ? routine.name : t('automations.new')}
        subtitle={<Link to="/automations" className="hover:underline">← {t('automations.title')}</Link>}
        actions={<>
          {routine && routine.access !== 'owner' && routine.access !== undefined ? (routine.access === 'editor'
            ? <Button variant="secondary" onClick={() => void rpc.call('routines.runNow', { routineId: routine.id }).then(() => toast(t('automations.queued'), 'success')).catch(e => toast((e as Error).message, 'error'))}>{t('automations.runNow')}</Button>
            : null) : routine && <>
            {routine.trigger.kind === 'webhook' && <Button variant="secondary" onClick={async () => setWebhookUrl((await rpc.call<{ webhookUrl: string }>('routines.rotateWebhook', { routineId: routine.id })).webhookUrl)}>{t('automations.webhook.rotate')}</Button>}
            <Button variant="secondary" onClick={() => void rpc.call('routines.runNow', { routineId: routine.id }).then(() => toast(t('automations.queued'), 'success')).catch(e => toast((e as Error).message, 'error'))}>{t('automations.runNow')}</Button>
            <Button variant="secondary" onClick={async () => {
              try {
                const r = await rpc.call<{ routine: Routine; webhookUrl?: string }>('routines.duplicate', { routineId: routine.id })
                toast(t('automations.duplicated'), 'success')
                navigate(`/automations/${r.routine.id}`)
                if (r.webhookUrl) setWebhookUrl(r.webhookUrl)
              } catch (e) { toast((e as Error).message, 'error') }
            }}>{t('automations.duplicate')}</Button>
            <Button variant="danger" onClick={async () => {
              if (!await confirmDialog(t('automations.confirmDelete', { name: routine.name }))) return
              await rpc.call('routines.delete', { routineId: routine.id })
              navigate('/automations')
            }}>{t('common.delete')}</Button>
          </>}
          <Button loading={saving} disabled={Boolean(routine?.access && routine.access !== 'owner')} onClick={() => void save()}>
            {routine ? t('common.save') : t('automations.create')}
          </Button>
        </>}
      />

      {routine && routine.access && routine.access !== 'owner' && <p className="mb-4 rounded-xl border border-cream-300 bg-cream-100 px-4 py-2 text-sm text-ink-400">{t('teams.routineReadOnly')}</p>}
      {!routine && <div className="mb-4 flex justify-end"><WorkspacePicker items={workspaces} value={ws} onChange={setWs} writableOnly /></div>}

      {Object.keys(errors).length > 0 && (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800" role="alert">
          <p className="font-semibold">{t('automations.validation.summary', { count: Object.keys(errors).length })}</p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-red-600">
            {Object.entries(errors).map(([key, msg]) => (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById(`field-${key}`) ?? document.querySelector(`[data-field="${key}"]`)
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLButtonElement) {
                        el.focus()
                      }
                    }
                  }}
                  className="underline hover:text-red-900"
                >
                  {msg}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid items-start gap-5 min-[1000px]:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-5">
          <Card title={t('automations.section.basics')}>
            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
              <Field label={<>{t('automations.name')}<Required /></>} error={errors.name}>
                {id => <Input id={id} data-field="name" value={draft.name} onChange={e => set({ name: e.target.value })} aria-invalid={Boolean(errors.name)} />}
              </Field>
              <div className="flex items-center pb-1 pt-6">
                <Checkbox checked={draft.enabled} onChange={e => set({ enabled: e.target.checked })} label={t('automations.enabled')} />
              </div>
            </div>
          </Card>

          <Card title={t('automations.section.trigger')}>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2" role="radiogroup">
                {(['cron', 'once', 'telegram', 'gmail', 'webhook'] as const).map(k => (
                  <button key={k} type="button" className={chipClass(draft.kind === k)} role="radio" aria-checked={draft.kind === k} onClick={() => set({ kind: k })}>
                    {t(`automations.trigger.${k}`)}
                  </button>
                ))}
              </div>
              {draft.kind === 'cron' && (
                <div className="space-y-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-300">{t('automations.schedule.title')}</p>
                  {/* Frequency first, then only the fields that frequency needs (docs/web-ui.md §Automations). */}
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('automations.schedule.title')}>
                    {PRESETS.map(p => (
                      <button key={p} type="button" role="radio" aria-checked={draft.preset === p} onClick={() => set({ preset: p })}
                        className={chipClass(draft.preset === p)}>
                        {t(`automations.schedule.${p}`)}
                      </button>
                    ))}
                  </div>

                  {draft.preset === 'weekly' && (
                    <div data-field="weekdays">
                      <ChipGroup label={t('automations.schedule.weekdays')} className={cn('flex flex-wrap gap-2', errors.weekdays && 'rounded-lg p-1 ring-1 ring-red-400')}>
                        {WEEKDAY_ORDER.map(d => {
                          const on = draft.weekdays.includes(d)
                          return (
                            <button key={d} type="button" aria-pressed={on}
                              onClick={() => set({ weekdays: (on ? draft.weekdays.filter(x => x !== d) : [...draft.weekdays, d]).sort((a, b) => a - b) })}
                              className={cn(chipClass(on), 'w-14 px-0')}>
                              {t(`automations.${WEEKDAY_LABEL[d]}`)}
                            </button>
                          )
                        })}
                      </ChipGroup>
                      {errors.weekdays && <p className="mt-1 text-xs text-red-600">{errors.weekdays}</p>}
                    </div>
                  )}

                  {draft.preset === 'monthly' && (
                    <div data-field="monthDays">
                      <ChipGroup label={t('automations.schedule.monthDays')} className={cn('grid max-w-[380px] grid-cols-7 gap-1.5', errors.monthDays ? 'p-1 rounded-lg ring-1 ring-red-400' : undefined)}>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => {
                          const on = draft.monthDays.includes(d)
                          return (
                            <button key={d} type="button" aria-pressed={on}
                              onClick={() => set({ monthDays: (on ? draft.monthDays.filter(x => x !== d) : [...draft.monthDays, d]).sort((a, b) => a - b) })}
                              className={cn(chipClass(on), 'px-0 tabular-nums')}>
                              {d}
                            </button>
                          )
                        })}
                      </ChipGroup>
                      {errors.monthDays && <p className="mt-1 text-xs text-red-600">{errors.monthDays}</p>}
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    {draft.preset === 'custom' ? (
                      <Field label={<>{t('automations.trigger.expr')}<Required /></>} hint={t('automations.trigger.exprHint')} error={errors.custom}>
                        {id => <Input id={id} data-field="custom" className="font-mono" value={draft.custom} onChange={e => set({ custom: e.target.value })} aria-invalid={Boolean(errors.custom)} />}
                      </Field>
                    ) : draft.preset === 'interval' ? (
                      <Field label={<>{t('automations.schedule.every')}<Required /></>}>{id => (
                        <div className="flex items-center gap-2">
                          <div className="w-24"><Input id={id} type="number" min={1} max={draft.intervalUnit === 'minutes' ? 59 : 23} value={draft.interval} onChange={e => set({ interval: Number(e.target.value) || 1 })} /></div>
                          <div className="flex gap-2" role="radiogroup" aria-label={t('automations.schedule.every')}>
                            {(['minutes', 'hours'] as const).map(u => (
                              <button key={u} type="button" role="radio" aria-checked={draft.intervalUnit === u} onClick={() => set({ intervalUnit: u })}
                                className={chipClass(draft.intervalUnit === u)}>
                                {t(`automations.schedule.${u}`)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}</Field>
                    ) : (
                      <Field label={<>{t('automations.schedule.time')}<Required /></>}>{id => <Input id={id} type="time" value={draft.time} onChange={e => set({ time: e.target.value })} />}</Field>
                    )}
                    <Field label={t('settings.timezone')} error={errors.tz}>{id => <Input id={id} data-field="tz" value={draft.tz} onChange={e => set({ tz: e.target.value })} aria-invalid={Boolean(errors.tz)} />}</Field>
                  </div>

                  <div className="text-xs text-ink-500">
                    {emptyDays ? <span className="text-red-600">{t('automations.schedule.pickDay')}</span>
                      : preview.error ? <span className="text-red-600">{preview.error}</span>
                        : preview.runs && <>{t('automations.trigger.nextRuns')}: {preview.runs.map(r => formatDateTime(r, i18n.language)).join(' · ')}</>}
                  </div>
                </div>
              )}
              {draft.kind === 'once' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={<>{t('automations.trigger.at')}<Required /></>} error={errors.at}>
                    {id => <Input id={id} data-field="at" type="datetime-local" value={draft.at} onChange={e => set({ at: e.target.value })} aria-invalid={Boolean(errors.at)} />}
                  </Field>
                  <Field label={t('settings.timezone')} error={errors.tz}>
                    {id => <Input id={id} data-field="tz" value={draft.tz} onChange={e => set({ tz: e.target.value })} aria-invalid={Boolean(errors.tz)} />}
                  </Field>
                </div>
              )}
              {draft.kind === 'telegram' && (telegramConnectors.length === 0 ? (
                <p className="text-sm text-ink-400">{t('automations.trigger.needTelegram')} <Link className="text-ginger-600 underline" to="/settings/connectors">{t('nav.connectors')}</Link></p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={<>{t('automations.trigger.bot')}<Required /></>} error={errors.connectorId}>
                    {id => (
                      <Select
                        id={id} data-field="connectorId" value={draft.connectorId} onChange={v => set({ connectorId: v })}
                        options={[{ value: '', label: '—' }, ...telegramConnectors.map(c => ({ value: c.id, label: c.name }))]}
                      />
                    )}
                  </Field>
                  <Field label={t('automations.trigger.chatIds')} hint={t('automations.trigger.chatIdsHint')}>{id => <Input id={id} value={draft.chatIds} onChange={e => set({ chatIds: e.target.value })} />}</Field>
                  <Field label={t('automations.trigger.textPrefix')}>{id => <Input id={id} value={draft.textPrefix} onChange={e => set({ textPrefix: e.target.value })} />}</Field>
                  <Field label={t('automations.trigger.noTextReply')} hint={t('automations.trigger.noTextReplyHint')}>{id => <Input id={id} value={draft.noTextReply} onChange={e => set({ noTextReply: e.target.value })} />}</Field>
                </div>
              ))}
              {draft.kind === 'webhook' && <p className="text-sm text-ink-400">{t('automations.webhook.hint')}</p>}
              {draft.kind === 'gmail' && (googleConnectors.length === 0 ? (
                <p className="text-sm text-ink-400">{t('automations.trigger.needGmail')} <Link className="text-ginger-600 underline" to="/settings/connectors">{t('nav.connectors')}</Link></p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label={<>{t('automations.trigger.gmailAccount')}<Required /></>} error={errors.connectorId}>
                    {id => (
                      <Select
                        id={id} data-field="connectorId" value={draft.connectorId} onChange={v => set({ connectorId: v })}
                        options={[{ value: '', label: '—' }, ...googleConnectors.map(c => ({ value: c.id, label: c.name }))]}
                      />
                    )}
                  </Field>
                  <Field label={t('automations.trigger.gmailQuery')} hint={t('automations.trigger.gmailQueryHint')}>{id => <Input id={id} value={draft.gmailQuery} onChange={e => set({ gmailQuery: e.target.value })} />}</Field>
                  <Field label={t('automations.trigger.gmailInterval')}>{id => <Input id={id} type="number" min={1} max={1440} value={draft.gmailInterval} onChange={e => set({ gmailInterval: Number(e.target.value) || 5 })} />}</Field>
                  <p className="text-xs text-ink-500 sm:col-span-3">{t('automations.trigger.gmailHint')}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card title={t('automations.section.task')}>
            <div className="space-y-4">
              <Field
                label={<>{t('automations.instructions')}<Required /></>}
                hint={t('automations.instructionsHint')}
                error={errors.instructions}
                action={
                  <button
                    type="button"
                    onClick={() => setInstructionsExpanded(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-ginger-300 bg-ginger-50 px-2 py-0.5 text-xs font-medium text-ginger-600 hover:bg-ginger-100 hover:text-ginger-700 hover:border-ginger-400 transition-colors"
                    title={t('automations.editor.expandInstructions')}
                  >
                    <IconExpand className="h-3 w-3" />
                    <span>{t('automations.editor.expandInstructions')}</span>
                  </button>
                }
              >
                {id => <Textarea id={id} data-field="instructions" rows={8} value={draft.instructions} onChange={e => set({ instructions: e.target.value })} aria-invalid={Boolean(errors.instructions)} />}
              </Field>
              <Field label={t('automations.mode')} hint={t(`automations.modeHint_${draft.mode}`)}>{id => (
                <Select
                  id={id} value={draft.mode} onChange={v => set({ mode: v })}
                  options={[
                    { value: 'agent', label: t('automations.mode_agent') },
                    { value: 'hybrid', label: t('automations.mode_hybrid') },
                    { value: 'compiled', label: t('automations.mode_compiled') },
                  ]}
                />
              )}</Field>
              <Field label={t('chat.model')} hint={t('automations.modelHint')}>{id => (
                <Select id={id} value={draft.model} onChange={v => set({ model: v })} options={allModels} />
              )}</Field>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{t('automations.tools')}</p>
                <div className="mb-2 flex gap-2">
                  <Button size="sm" variant={draft.toolsMode === 'default' ? 'primary' : 'outline'} onClick={() => set({ toolsMode: 'default' })}>{t('automations.toolsDefault')}</Button>
                  <Button size="sm" variant={draft.toolsMode === 'custom' ? 'primary' : 'outline'} onClick={() => set({ toolsMode: 'custom', tools: draft.tools.length ? draft.tools : available.filter(a => a.defaultPolicy === 'allow').map(a => a.name) })}>{t('automations.toolsCustom')}</Button>
                </div>
                {draft.toolsMode === 'default' ? <p className="text-xs text-ink-500">{t('automations.toolsDefaultHint')}</p> : (<>
                  <p className="mb-3 text-xs text-ink-500">{t('automations.toolsCustomHint')}</p>
                  {/* A table, not a grid of unlabelled selects: the header names the permission column
                      and the tool name can never be squeezed out (docs/web-ui.md §2). */}
                  <Toolbar>
                    <SearchBox value={toolQuery} onChange={setToolQuery} placeholder={t('automations.toolsSearch')} />
                    <FilterChips
                      value={toolFilter} onChange={v => setToolFilter(v)} label={t('automations.tools')}
                      options={[{ value: 'all', label: t('common.all') }, { value: 'on', label: t('automations.toolsSelected') }]}
                    />
                    <Count>{t('automations.toolsCount', { count: draft.tools.length, total: available.length })}</Count>
                  </Toolbar>
                  {shownTools.length === 0 ? (
                    <p className="py-6 text-center text-sm text-ink-400">{t('automations.toolsNone')}</p>
                  ) : (
                    <Table minWidth={620}>
                      <thead>
                        <tr>
                          <Th rail />
                          <Th>{t('automations.tools')}</Th>
                          <Th>{t('automations.toolsSource')}</Th>
                          <Th>{t('connectors.default')}</Th>
                          <Th>{t('connectors.policy')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {shownTools.map(a => {
                          const on = draft.tools.includes(a.name)
                          return (
                            <Row key={a.name} rail={on ? POLICY_RAIL[a.defaultPolicy] : undefined}>
                              <Td>
                                <label className="flex items-center gap-2.5">
                                  <input
                                    type="checkbox" checked={on} aria-label={a.name}
                                    onChange={e => set({ tools: e.target.checked ? [...draft.tools, a.name] : draft.tools.filter(x => x !== a.name) })}
                                  />
                                  <span className="font-mono text-xs font-semibold">{a.name}</span>
                                </label>
                              </Td>
                              <Td><span className="text-xs text-ink-500">{a.source}</span></Td>
                              <Td><span className="text-xs text-ink-400">{t(`connectors.policy_${a.defaultPolicy}`)}</span></Td>
                              <Td>
                                <div className="w-40">
                                  <Select
                                    size="sm" label={`${t('connectors.policy')}: ${a.name}`} disabled={!on}
                                    value={draft.toolPolicy[a.name] ?? ''}
                                    onChange={v => {
                                      const next = { ...draft.toolPolicy }
                                      if (v) next[a.name] = v as 'allow' | 'ask' | 'deny'
                                      else delete next[a.name]
                                      set({ toolPolicy: next })
                                    }}
                                    options={[
                                      { value: '', label: `${t(`connectors.policy_${a.defaultPolicy}`)} (${t('connectors.default')})` },
                                      { value: 'allow', label: t('connectors.policy_allow') },
                                      { value: 'ask', label: t('connectors.policy_ask') },
                                      { value: 'deny', label: t('connectors.policy_deny') },
                                    ]}
                                  />
                                </div>
                              </Td>
                            </Row>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </>)}
              </div>
            </div>
          </Card>

          <Card title={t('kb.automationScope')}>
            <p className="mb-3 text-sm text-ink-400">{t('kb.automationScopeHint')}</p>
            <fieldset disabled={Boolean(routine?.access && routine.access !== 'owner')}>
              <CollectionChecklist items={readable} value={draft.kbCollections} onChange={ids => set({ kbCollections: ids })} />
            </fieldset>
          </Card>

          <Card title={t('automations.section.limits')}>
            <div className="grid gap-4 sm:grid-cols-4">
              <Field label={t('automations.limits.runsPerHour')}>{id => <Input id={id} type="number" min={1} value={draft.limits.maxRunsPerHour} onChange={e => set({ limits: { ...draft.limits, maxRunsPerHour: Number(e.target.value) } })} />}</Field>
              <Field label={t('automations.limits.steps')}>{id => <Input id={id} type="number" min={1} max={500} value={draft.limits.maxStepsPerRun} onChange={e => set({ limits: { ...draft.limits, maxStepsPerRun: Number(e.target.value) } })} />}</Field>
              <Field label={t('automations.limits.cost')}>{id => <Input id={id} type="number" min={0.01} step="0.01" value={draft.limits.maxCostPerRunUsd} onChange={e => set({ limits: { ...draft.limits, maxCostPerRunUsd: Number(e.target.value) } })} />}</Field>
              <Field label={t('automations.limits.timeout')}>{id => <Input id={id} type="number" min={10} value={draft.limits.timeoutSec} onChange={e => set({ limits: { ...draft.limits, timeoutSec: Number(e.target.value) } })} />}</Field>
              <Field label={t('automations.session')}>{id => (
                <Select
                  id={id} value={draft.session} onChange={v => set({ session: v })}
                  options={[
                    { value: 'per-run', label: t('automations.sessionPerRun') },
                    { value: 'persistent', label: t('automations.sessionPersistent') },
                  ]}
                />
              )}</Field>
              <Field label={t('automations.missed')}>{id => (
                <Select
                  id={id} value={draft.missedRunPolicy} onChange={v => set({ missedRunPolicy: v })}
                  options={[
                    { value: 'skip', label: t('automations.missedSkip') },
                    { value: 'run-once', label: t('automations.missedRunOnce') },
                  ]}
                />
              )}</Field>
              <div className="flex flex-wrap items-center gap-6 pt-2">
                <Checkbox checked={draft.notifyFailure} onChange={e => set({ notifyFailure: e.target.checked })} label={t('automations.notifyFailure')} />
                <Checkbox checked={draft.notifySuccess} onChange={e => set({ notifySuccess: e.target.checked })} label={t('automations.notifySuccess')} />
              </div>
            </div>
          </Card>

          {routine && draft.mode !== 'agent' && <ExtractorPanel routine={routine} />}
        </div>
        <aside className="min-w-0 space-y-5 min-[1000px]:sticky min-[1000px]:top-4">
          <Card title={t('automations.editor.summary.title')} subtitle={t('automations.editor.summary.hint')}>
            <dl className="space-y-2.5">
              <InfoRow label={t('automations.enabled')} value={draft.enabled ? t('automations.editor.summary.on') : t('automations.editor.summary.off')} />
              <InfoRow label={t('automations.section.trigger')} value={triggerSummary(buildTrigger(draft), t)} />
              <InfoRow label={t('automations.column.next')} value={nextRun ? formatDateTime(nextRun, i18n.language) : '—'} />
              <InfoRow label={t('chat.model')} value={modelLabel} />
              <InfoRow label={t('automations.tools')} value={toolsLabel} />
              <InfoRow label={t('automations.limits.cost')} value={formatUsd(draft.limits.maxCostPerRunUsd)} />
              <InfoRow label={t('automations.session')} value={draft.session === 'persistent' ? t('automations.sessionPersistent') : t('automations.sessionPerRun')} />
            </dl>
          </Card>
          {routine && (
            <Card title={t('automations.runs')}>
              {runs.length === 0 ? <p className="text-[13px] text-ink-500">{t('automations.noRuns')}</p> : (
                <ul className="-my-1 divide-y divide-line">
                  {runs.slice(0, 5).map(r => (
                    <li key={r.id} className="flex items-center gap-2 py-2">
                      <RunStatusBadge status={r.status} />
                      <Link to={`/automations/runs/${r.id}`} className="min-w-0 flex-1 truncate text-[12.5px] text-ink-300 tabular-nums hover:underline">{formatDateTime(r.queuedAt, i18n.language)}</Link>
                      {r.costUsd !== null && <span className="text-[11.5px] text-ink-500 tabular-nums">{formatUsd(r.costUsd)}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </aside>
      </div>

      <Modal open={webhookUrl !== null} title={t('automations.webhook.title')} onClose={() => setWebhookUrl(null)}
        footer={<Button onClick={() => { void navigator.clipboard?.writeText(webhookUrl ?? ''); toast(t('common.copied'), 'success') }}>{t('common.copy')}</Button>}>
        <p className="mb-2 text-sm text-ink-400">{t('automations.webhook.once')}</p>
        <Input readOnly value={webhookUrl ?? ''} onFocus={e => e.target.select()} aria-label={t('automations.webhook.title')} />
        <pre className="code-block mt-3 overflow-x-auto rounded-lg p-2.5 font-mono text-xs">{`curl -X POST '${webhookUrl}?wait=30' \\\n  -H 'content-type: application/json' -d '{"hello":"world"}'`}</pre>
      </Modal>

      <MarkdownOverlayEditor
        open={instructionsExpanded}
        value={draft.instructions}
        onChange={val => set({ instructions: val })}
        onClose={() => setInstructionsExpanded(false)}
      />
    </div>
  )
}
